import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

/**
 * Laboratory stages, configured per tenant.
 *
 * There is no fixed Pre/Post. A tenant defines its own stages, which tests each
 * one orders, and which stages must have results in before a later one can be
 * ordered.
 */

/**
 * Where a stage test's ordering provider (ORC-12 / OBR-16) comes from. Only
 * these two can be filled by an order today; the person placing the order and
 * the patient's ADT attending follow (z8nrz7ffvz, second delivery).
 */
export type LisProviderSource = 'fixed' | 'choose'

/** A stage test's description and ordering provider, as saved and as sent. */
export type LisStageTestSetup = {
  /** Sent to Epic as the test name (OBR-4.2). */
  description?: string | null
  /** Null: not set up, and orders of this test carry no provider. */
  providerSource?: LisProviderSource | null
  /** The fixed provider, or the default offered when choosing on the order. */
  providerNpi?: string | null
  providerLastName?: string | null
  providerFirstName?: string | null
  providerMiddleName?: string | null
  /** Refuse the order when no provider is found. */
  blockIfNoProvider?: boolean
}

export type LisStageTestCode = LisStageTestSetup & {
  id: string
  testCode: string
  /** Ticked when the stage is chosen. */
  defaultSelected: boolean
  sortOrder: number
  active: boolean
}

export type LisStage = {
  id: string
  name: string
  description: string | null
  sortOrder: number
  active: boolean
  /** Stages whose results must be in before this one can be ordered. */
  predecessors: { id: string; name: string }[]
  testCodes: LisStageTestCode[]
}

/** Whether a stage can be ordered for one product, and why not. */
export type LisStageAvailability = {
  id: string
  name: string
  sortOrder: number
  selectable: boolean
  reason: 'inactive' | 'missing_predecessor' | null
  missing: string[]
  blockedReason: string | null
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    headers: authHeaders(init?.body ? { 'Content-Type': 'application/json' } : undefined),
    ...init,
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      const parsed = JSON.parse(text) as { message?: string | string[] }
      if (parsed.message) message = Array.isArray(parsed.message) ? parsed.message.join(', ') : parsed.message
    } catch {
      /* leave raw text */
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  const text = await res.text()
  return (text ? JSON.parse(text) : undefined) as T
}

const ROOT = '/integrations/lis/stages'

export const lisStagesApi = {
  list: () => request<LisStage[]>(ROOT),

  availability: (productId: string) =>
    request<LisStageAvailability[]>(`${ROOT}/availability?productId=${encodeURIComponent(productId)}`),

  create: (body: { name: string; description?: string; sortOrder?: number }) =>
    request<LisStage>(ROOT, { method: 'POST', body: JSON.stringify(body) }),

  update: (
    id: string,
    body: { name?: string; description?: string | null; sortOrder?: number; active?: boolean },
  ) => request<LisStage>(`${ROOT}/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  remove: (id: string) => request<{ removed: boolean }>(`${ROOT}/${id}`, { method: 'DELETE' }),

  addPredecessor: (id: string, predecessorId: string) =>
    request<LisStage>(`${ROOT}/${id}/predecessors`, {
      method: 'POST',
      body: JSON.stringify({ predecessorId }),
    }),

  removePredecessor: (id: string, predecessorId: string) =>
    request<LisStage>(`${ROOT}/${id}/predecessors/${predecessorId}`, { method: 'DELETE' }),

  addTestCode: (
    id: string,
    body: LisStageTestSetup & { testCode: string; defaultSelected?: boolean; sortOrder?: number },
  ) => request<LisStage>(`${ROOT}/${id}/test-codes`, { method: 'POST', body: JSON.stringify(body) }),

  updateTestCode: (
    testCodeId: string,
    body: LisStageTestSetup & { defaultSelected?: boolean; sortOrder?: number; active?: boolean },
  ) =>
    request<LisStage>(`${ROOT}/test-codes/${testCodeId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  removeTestCode: (testCodeId: string) =>
    request<LisStage>(`${ROOT}/test-codes/${testCodeId}`, { method: 'DELETE' }),

  copyTestCodes: (toStageId: string, fromStageId: string) =>
    request<{ copied: number; skipped: number }>(`${ROOT}/${toStageId}/test-codes/copy`, {
      method: 'POST',
      body: JSON.stringify({ fromStageId }),
    }),
}
